import { readFragment, ResultOf } from "gql.tada";
import { SlideCarouselSectionFragment } from "@queries/fragments/SlideCarouselSectionFragment";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Link } from "@components/shared/core/Link";
import { IconChevronRight } from "@hawkins/assets";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, Document as RichTextDocument } from "@contentful/rich-text-types";
import { useFootnoteContext } from "@utils/FootnoteContext";

type SlideCarouselData = ResultOf<typeof SlideCarouselSectionFragment>;
type SlideItem = NonNullable<
  NonNullable<SlideCarouselData["itemsCollection"]>["items"][number]
>;

export const FeaturedCard = ({
  item,
  slideRef,
  onMouseEnter,
  onMouseLeave,
}: {
  item: SlideItem;
  slideRef: (node: HTMLDivElement | null) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  const ctaLink = item.ctaLink ? readFragment(LinkFragment, item.ctaLink) : null;
  const { getNumber } = useFootnoteContext();

  const content = (
    <article className="relative w-[85vw] sm:w-[332px] min-h-[370px] rounded-lg bg-neutral-cream/15 backdrop-blur-[25px] flex flex-col justify-between py-[70px] px-[24px]">
      <div className="flex flex-col gap-9 items-start w-full h-full justify-between">
        <div className="flex flex-col gap-6 items-start w-full">
          {item.title && (
            <p className="text-palette-current-highlight font-['Netflix_Sans_Title'] font-medium text-[40px] leading-[0.94] tracking-[-0.02em] capitalize">
              {item.title}
            </p>
          )}
          {!!item?.description?.json && documentToReactComponents(
            item.description.json as RichTextDocument,
            {
              renderNode: {
                [BLOCKS.PARAGRAPH]: (_, children) => (
                  <p className="text-neutral-cream text-body-small leading-[1.4] max-w-[247px]">
                    {children}
                  </p>
                ),
                [INLINES.EMBEDDED_ENTRY]: (node) => {
                  const id = node.data?.target?.sys?.id as string | undefined;
                  if (!id) return null;
                  const number = getNumber(id);
                  if (number === null) return null;
                  return <sup className="text-[0.7em] leading-none">{number}</sup>;
                },
              },
            }
          )}
        </div>
        {ctaLink && (
          <div className="flex items-center gap-2 text-palette-current-highlight">
            <span className="text-button-large tracking-[-0.01em]">
              {ctaLink.text ?? "Read More"}
            </span>
            <IconChevronRight size="small" />
          </div>
        )}
      </div>
    </article>
  );

  return (
    <div
      ref={slideRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="snap-start shrink-0"
    >
      {ctaLink ? (
        <Link link={ctaLink} className="block no-underline">
          {content}
        </Link>
      ) : (
        content
      )}
    </div>
  );
};
